import {
  attributeOptionMatch,
  isFileAttributeType,
  itemScopeApplies,
  NODE_ATTRIBUTE_TARGETS,
  NODE_ATTRIBUTE_TYPES,
  normaliseAttributeOptions,
  qualifyItemScope,
  type GeneratedDefinition,
  type GeneratedNodeAttribute,
  type GeneratedStatus,
  type NodeAttributeTarget,
  type NodeAttributeType,
} from "@keydials/api-client";
import { renameFieldset } from "./attributeFieldsets";
import {
  attributeTypeSupportsChildSource,
  attributeTypeSupportsDefault,
  attributeTypeSupportsMultiple,
  attributeTypeSupportsOptions,
  attributeTypeSupportsPlacement,
  attributeTypeSupportsUserValues,
} from "./attributeTypeRules";
import { slug } from "./keys";
import {
  canMoveFieldset,
  canMoveNodeAttribute,
  moveFieldset,
  moveNodeAttribute,
  normaliseAttributeFieldsetRuns,
  restampNodeAttributeOrder,
} from "./nodeAttributeOrder";
import type { AddedNode } from "./structureOps";
import { uniqueKey } from "./wizardSupport";

// ---------------------------------------------------------------------------
// Field edits for the builder's Details stage (KEY-560, Phase 3): the extra details a dial asks for (node attributes), each
// addressed by its level (`target`) and its key.
//
// The same contract as the structure edits. Every function takes the model and returns a new one, spreading only what it changes,
// so a setting a template or an admin put on a field that the builder doesn't show travels through untouched: a field is never
// rebuilt from form state. Keys are minted once and never change on rename. Where an edit leaves a setting the server would drop
// (a type that can't carry it, a heading that has gone), the edit drops it too, so what the builder shows is what gets saved.
// An edit that can't apply (a field that isn't there, a refused retype) hands back the model it was given.
// ---------------------------------------------------------------------------

/** Room for a key: the server stores 80 characters (`NodeAttributeDefinitions.AttributeKey`), less a `-n` suffix. */
const KEY_ROOM = 72;

/** One place up (-1) or down (1). */
export type FieldDirection = -1 | 1;

const fieldsOf = (model: GeneratedDefinition): GeneratedNodeAttribute[] => model.nodeAttributes ?? [];

const indexOfField = (fields: readonly GeneratedNodeAttribute[], target: string, key: string) =>
  fields.findIndex((f) => f.target === target && f.key === key);

const titleOf = (field: GeneratedNodeAttribute): string | null => field.fieldsetTitle?.trim() || null;

// The server reads a field's level and type ignoring case, and skips a field whose level or type it doesn't know.
const sameWord = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const knownTarget = (target: string) => NODE_ATTRIBUTE_TARGETS.some((t) => sameWord(t, target));
const knownType = (type: string) => NODE_ATTRIBUTE_TYPES.some((t) => sameWord(t, type));

/** Whether a setting holds anything. A cleared setting (null, false, empty) is left where it is rather than churned. */
const isSet = (value: unknown) =>
  value !== undefined && value !== null && value !== false && value !== "" && !(Array.isArray(value) && value.length === 0);

/** `obj` without whichever of `keys` hold something; the same object back when none do. */
function without<T extends object>(obj: T, keys: readonly (keyof T)[]): T {
  const present = keys.filter((k) => isSet(obj[k]));
  if (present.length === 0) return obj;
  const next = { ...obj } as unknown as Record<PropertyKey, unknown>;
  for (const k of present) delete next[k as PropertyKey];
  return next as unknown as T;
}

function mapField(
  model: GeneratedDefinition,
  target: string,
  key: string,
  change: (field: GeneratedNodeAttribute) => GeneratedNodeAttribute,
): GeneratedDefinition {
  const fields = fieldsOf(model);
  const index = indexOfField(fields, target, key);
  if (index < 0) return model;
  const changed = change(fields[index]);
  if (changed === fields[index]) return model;
  const next = [...fields];
  next[index] = changed;
  return { ...model, nodeAttributes: next };
}

// ---- adding ---------------------------------------------------------------------------------------------------------------

/** What a new field starts as: where it is asked, what kind of answer it takes, and what it's called. */
export interface NewField {
  target: NodeAttributeTarget;
  type: NodeAttributeType;
  label: string;
}

/**
 * The keys already taken at a level, compared the way the server compares them when it refuses a clash (`PopulateNodeAttributes`):
 * trimmed and ignoring case, because SQL Server's collation reads "Owner" and "owner " as one row. A key only has to be unique
 * within its level, so a section and an action point can each have an `owner`.
 */
function takenKeys(fields: readonly GeneratedNodeAttribute[], target: string): Set<string> {
  return new Set(fields.filter((f) => sameWord(f.target, target) && f.key.trim()).map((f) => f.key.trim().toLowerCase()));
}

/**
 * Adds a field at the end of the dial's fields, which is the end of its own level's, and returns the key it was given so the
 * builder can select it. The key is the label's slug, suffixed `-2`, `-3`… when its level already has it.
 *
 * Refused (the model back, with an empty key) for a type or level the server doesn't know, for a table (the builder recognises a
 * template's tables but never makes one), and for a per-item field on a dial without checklists, which has no items to ask.
 */
export function addField(model: GeneratedDefinition, field: NewField): AddedNode {
  if (field.type === "Table" || !knownType(field.type) || !knownTarget(field.target)) return { model, key: "" };
  if (field.target === "Item" && model.kind !== "Itemised") return { model, key: "" };
  const fields = fieldsOf(model);
  const key = uniqueKey(slug(field.label, "field").slice(0, KEY_ROOM).replace(/-+$/, ""), takenKeys(fields, field.target));
  const added: GeneratedNodeAttribute = { key, label: field.label, target: field.target, type: field.type, order: fields.length };
  return { model: { ...model, nodeAttributes: restampNodeAttributeOrder([...fields, added]) }, key };
}

// ---- editing --------------------------------------------------------------------------------------------------------------

/**
 * The settings {@link updateField} spreads over a field. Not its identity (key, level), and not what has an edit of its own that
 * keeps the server's rules: its type ({@link setFieldType}), its heading ({@link setGroup}), its position (`order`, {@link moveField})
 * and which items it's on ({@link setOnlySomeItems}, {@link toggleItem}).
 */
export type FieldPatch = Partial<Omit<GeneratedNodeAttribute, "key" | "target" | "type" | "order" | "fieldsetTitle" | "appliesToKeys">>;

/** Changes a field's settings by spreading the patch over the field as it is, so anything the patch doesn't name survives. */
export function updateField(model: GeneratedDefinition, target: string, key: string, patch: FieldPatch): GeneratedDefinition {
  return mapField(model, target, key, (field) => {
    const next = { ...field, ...patch };
    // Starts folded only means something under a heading; the server drops it from a field without one.
    return titleOf(next) === null ? without(next, ["fieldsetStartsCollapsed"]) : next;
  });
}

/** The server's twin, `RichTextHasContent`: text outside the tags, ignoring whitespace and non-breaking spaces. */
function richTextHasContent(value: string): boolean {
  const text = value
    .replace(/<[^>]*(>|$)/g, "")
    .replaceAll("&nbsp;", " ")
    .replaceAll("&#160;", " ")
    .replaceAll("&#xa0;", " ");
  return text.trim().length > 0;
}

/** Whether a date reads as the ISO `yyyy-MM-dd` of a real day, which is all a Date field's default may be. */
function isIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const date = new Date(0);
  date.setUTCFullYear(year, month, day);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month && date.getUTCDate() === day;
}

/**
 * The suggested answer a field of `type` keeps, mirroring the server's `ClampDefaultValue`: only Text, RichText, Date and List carry
 * one; a Date's must be a real ISO date and a RichText's must have content; a closed List's must be one of its options, in the
 * option's own spelling. Undefined when there's nothing to keep.
 */
function keptDefault(field: GeneratedNodeAttribute, type: string): string | undefined {
  const value = field.defaultValue?.trim();
  if (!value || !attributeTypeSupportsDefault(type)) return undefined;
  if (type === "List") {
    return field.allowUserValues ? field.defaultValue! : (attributeOptionMatch(normaliseAttributeOptions(field.options), value) ?? undefined);
  }
  if (type === "Date" && !isIsoDate(value)) return undefined;
  if (type === "RichText" && !richTextHasContent(value)) return undefined;
  return field.defaultValue!;
}

/**
 * Changes a field's type, clearing what the new type can't carry the way the server's `NodeAttributeDefinition` constructor does:
 * options only on a List (or a Table), "people can add their own" only on a List, more than one file only on a Document or Image,
 * placements and "a file here sets a status" only where a file can or can't be held, a child dial's answer only on a type that can
 * cross dials, and a suggested answer per {@link keptDefault}.
 *
 * Refused for a table either way (decision 6): a template's table can be renamed, moved, grouped and removed, but its columns mean
 * nothing to any other type, and no other type becomes one here. Refused, too, for a type the server doesn't know.
 */
export function setFieldType(model: GeneratedDefinition, target: string, key: string, type: NodeAttributeType): GeneratedDefinition {
  if (type === "Table" || type === "Person" || type === "Contact" || type === "ResponsiblePerson" || !knownType(type)) return model;
  return mapField(model, target, key, (field) => {
    if (field.type === type || field.type === "Table" || field.type === "Person" || field.type === "Contact" || field.type === "ResponsiblePerson") return field;
    const cleared: (keyof GeneratedNodeAttribute)[] = [];
    if (!attributeTypeSupportsOptions(type)) cleared.push("options");
    if (!attributeTypeSupportsUserValues(type)) cleared.push("allowUserValues");
    if (!attributeTypeSupportsMultiple(type)) cleared.push("allowMultiple");
    if (!attributeTypeSupportsPlacement(type)) cleared.push("shownAtKeys");
    if (!isFileAttributeType(type)) cleared.push("uploadSetsStatusKey", "uploadSetsDimensionKey");
    if (!attributeTypeSupportsChildSource(type)) cleared.push("sourceChildDefinitionId", "sourceAttributeKey", "sourceNodePath");

    // Judged against the settings the new type keeps: a closed list's default has to match the options that survive.
    const retyped = without({ ...field, type }, cleared);
    return withKeptDefault(retyped);
  });
}

/** The field with its suggested answer judged again by {@link keptDefault}: cleared, re-spelled to its option, or as it was. */
function withKeptDefault(field: GeneratedNodeAttribute): GeneratedNodeAttribute {
  const kept = keptDefault(field, field.type);
  if (kept === undefined) return without(field, ["defaultValue"]);
  return kept === field.defaultValue ? field : { ...field, defaultValue: kept };
}

/**
 * Sets a choice's options (or a table's columns) as given, without tidying them: a box being typed in, or one just added, is kept
 * as it is, and the builder tidies with `normaliseAttributeOptions` when the box is left. The suggested answer is judged again
 * against what the options now are, as the server will on save: a closed list's default that no option matches any more is
 * cleared, and one that matches an option in another spelling takes the option's.
 *
 * Refused for a type that carries no options, which the server would drop them from.
 */
export function setOptions(model: GeneratedDefinition, target: string, key: string, options: readonly string[]): GeneratedDefinition {
  return mapField(model, target, key, (field) => {
    if (!attributeTypeSupportsOptions(field.type)) return field;
    const current = field.options ?? [];
    if (current.length === options.length && current.every((o, i) => o === options[i])) return field;
    return withKeptDefault({ ...field, options: [...options] });
  });
}

/**
 * Turns "people can add their own" on or off for a choice. Off, the suggested answer has to be one of the options, so it is judged
 * again as the server will on save: cleared when no option matches, re-spelled to the option when one does. On, any answer is kept.
 *
 * Refused for any type but a choice, which is the only one the switch means anything on.
 */
export function setAllowUserValues(model: GeneratedDefinition, target: string, key: string, on: boolean): GeneratedDefinition {
  return mapField(model, target, key, (field) => {
    if (!attributeTypeSupportsUserValues(field.type) || Boolean(field.allowUserValues) === on) return field;
    return withKeptDefault({ ...field, allowUserValues: on });
  });
}

// ---- removing -------------------------------------------------------------------------------------------------------------

/** A status that couldn't be set until the removed field had a value. */
export interface StatusGateReference {
  /** The dial's own palette, a custom palette its action points use, or an item scale's. */
  palette: "dial" | "custom" | "scale";
  paletteName: string;
  /** The scale's key, for a status on an item scale. */
  dimensionKey?: string;
  statusKey: string;
  statusName: string;
}

/** Another field that was paired with the removed one ("fill both or neither"). */
export interface PairingReference {
  target: string;
  key: string;
  label: string;
}

/** A reset schedule that also cleared the removed field. */
export interface ResetClearReference {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  dimensionKey?: string | null;
}

/** Everything else that named a removed field, and that went with it. */
export interface FieldReferences {
  statusGates: StatusGateReference[];
  pairings: PairingReference[];
  resetClears: ResetClearReference[];
}

/** A model with a field removed, and what else named it, so the confirm can say so. */
export interface RemovedField {
  model: GeneratedDefinition;
  removed: FieldReferences;
}

/**
 * Removes a field, and what named it.
 *
 * - Status gates: a status names the fields it waits on by key, among the fields of the level its palette scores (the dial's own
 *   palette and its custom palettes gate on action point fields, an item scale's on item fields). The server drops a gate key no
 *   field declares, so the removal does too.
 * - Pairings: another field's `requiresAttributeKey`, which the server drops when no field of any level declares the key.
 * - Pre-fills (KEY-752): a Contact field's `fillsAttributeKey`, dropped when no field at the Contact field's level declares the key.
 * - Reset schedules: a schedule's `clearAttributeKeys`, judged at the level it resets (an item, or an action point). The server
 *   doesn't drop these on save; it refuses to publish a schedule that clears a field nothing declares, so the removal takes the
 *   key out rather than leave that for later.
 *
 * Keys are only unique within a level, so a reference is left alone while another field that could still answer it remains.
 */
export function removeField(model: GeneratedDefinition, target: string, key: string): RemovedField {
  const removed: FieldReferences = { statusGates: [], pairings: [], resetClears: [] };
  const fields = fieldsOf(model);
  const index = indexOfField(fields, target, key);
  if (index < 0) return { model, removed };

  const remaining = restampNodeAttributeOrder(fields.filter((_, i) => i !== index));
  const declares = (level: string | null) =>
    remaining.some((f) => f.key === key && f.key.trim() !== "" && knownType(f.type) && knownTarget(f.target) && (level === null || sameWord(f.target, level)));
  const level = sameWord(target, "Segment") ? "Segment" : sameWord(target, "Item") ? "Item" : null;
  const next: GeneratedDefinition = { ...model, nodeAttributes: remaining };

  const ungate = (statuses: GeneratedStatus[], where: Omit<StatusGateReference, "statusKey" | "statusName">): GeneratedStatus[] => {
    if (!statuses.some((s) => s.requiredAttributeKeys?.includes(key))) return statuses;
    return statuses.map((s) => {
      if (!s.requiredAttributeKeys?.includes(key)) return s;
      removed.statusGates.push({ ...where, statusKey: s.key, statusName: s.name });
      const kept = s.requiredAttributeKeys.filter((k) => k !== key);
      return kept.length > 0 ? { ...s, requiredAttributeKeys: kept } : without(s, ["requiredAttributeKeys"]);
    });
  };

  if (level !== null && !declares(level)) {
    if (level === "Segment") {
      const statuses = ungate(model.statusSet.statuses, { palette: "dial", paletteName: model.statusSet.name });
      if (statuses !== model.statusSet.statuses) next.statusSet = { ...model.statusSet, statuses };
      if (model.customPalettes) {
        next.customPalettes = model.customPalettes.map((p) => {
          const custom = ungate(p.statuses, { palette: "custom", paletteName: p.name });
          return custom === p.statuses ? p : { ...p, statuses: custom };
        });
      }
    } else if (model.dimensions) {
      next.dimensions = model.dimensions.map((d) => {
        const statuses = ungate(d.palette.statuses, { palette: "scale", paletteName: d.palette.name, dimensionKey: d.key });
        return statuses === d.palette.statuses ? d : { ...d, palette: { ...d.palette, statuses } };
      });
    }

    if (model.resetSchedules) {
      next.resetSchedules = model.resetSchedules.map((r) => {
        if ((r.itemKey ? "Item" : "Segment") !== level || !r.clearAttributeKeys?.includes(key)) return r;
        removed.resetClears.push({ sectionKey: r.sectionKey, segmentKey: r.segmentKey, itemKey: r.itemKey, dimensionKey: r.dimensionKey });
        const kept = r.clearAttributeKeys.filter((k) => k !== key);
        return kept.length > 0 ? { ...r, clearAttributeKeys: kept } : without(r, ["clearAttributeKeys"]);
      });
    }
  }

  if (key.trim() !== "" && !declares(null)) {
    next.nodeAttributes = remaining.map((f) => {
      if (f.requiresAttributeKey?.trim() !== key) return f;
      removed.pairings.push({ target: f.target, key: f.key, label: f.label });
      return without(f, ["requiresAttributeKey"]);
    });
  }

  // KEY-752: a Contact field that pre-filled the removed field from its contact's address fills nothing now. Judged at the Contact
  // field's own level, as the server judges it, and dropped quietly — nothing was stored by it, so there is nothing to report.
  if (key.trim() !== "" && (next.nodeAttributes ?? []).some((f) => f.fillsAttributeKey?.trim() === key && !declares(f.target))) {
    next.nodeAttributes = (next.nodeAttributes ?? []).map((f) =>
      f.fillsAttributeKey?.trim() === key && !declares(f.target) ? without(f, ["fillsAttributeKey"]) : f);
  }

  return { model: next, removed };
}

// ---- moving ---------------------------------------------------------------------------------------------------------------

// A level's fields are drawn groups first, then the loose fields (KEY-558), and a move keeps to that: a grouped field moves among
// its group, a loose field among the loose ones, a group past the groups beside it. Both moves gather split groups first, so
// "move this group" always carries every member.

/** Whether {@link moveField} would move the field. */
export function canMoveField(model: GeneratedDefinition, target: string, key: string, direction: FieldDirection): boolean {
  const fields = normaliseAttributeFieldsetRuns(fieldsOf(model), target);
  const index = indexOfField(fields, target, key);
  return index >= 0 && canMoveNodeAttribute(fields, index, direction);
}

/** Moves a field one place among its peers: within its group when it has one, otherwise among the level's loose fields. */
export function moveField(model: GeneratedDefinition, target: string, key: string, direction: FieldDirection): GeneratedDefinition {
  if (!canMoveField(model, target, key, direction)) return model;
  const fields = normaliseAttributeFieldsetRuns(fieldsOf(model), target);
  return { ...model, nodeAttributes: moveNodeAttribute(fields, indexOfField(fields, target, key), direction) };
}

/** Whether {@link moveGroup} would move the group. */
export function canMoveGroup(model: GeneratedDefinition, target: string, fieldsetTitle: string, direction: FieldDirection): boolean {
  return canMoveFieldset(normaliseAttributeFieldsetRuns(fieldsOf(model), target), target, fieldsetTitle, direction);
}

/** Moves a whole group, every member with it, past the group above or below it at its level. */
export function moveGroup(model: GeneratedDefinition, target: string, fieldsetTitle: string, direction: FieldDirection): GeneratedDefinition {
  if (!canMoveGroup(model, target, fieldsetTitle, direction)) return model;
  const fields = normaliseAttributeFieldsetRuns(fieldsOf(model), target);
  return { ...model, nodeAttributes: moveFieldset(fields, target, fieldsetTitle, direction) };
}

// ---- groups ---------------------------------------------------------------------------------------------------------------

// A group is a heading its fields share (KEY-436): the title is its whole identity. Assigning a field is per field (KEY-551), so a
// field can leave one group for another without the rest following; renaming the group as a whole is its own edit.

/**
 * Puts one field under a heading, as typed, or takes it out of its group with null or a blank title. Taking it out also clears
 * "starts folded", which means nothing without a heading and which the server drops. The title isn't trimmed and the runs aren't
 * gathered while typing; {@link normaliseFieldsetRuns} does both when the box is left.
 */
export function setGroup(model: GeneratedDefinition, target: string, key: string, title: string | null): GeneratedDefinition {
  return mapField(model, target, key, (field) =>
    title === null || title.trim() === ""
      ? without(field, ["fieldsetTitle", "fieldsetStartsCollapsed"])
      : field.fieldsetTitle === title
        ? field
        : { ...field, fieldsetTitle: title },
  );
}

/**
 * Tidies one level's groups the way the server and the user app will read them: headings trimmed, "starts folded" cleared where
 * there's no heading, and every group's members gathered into one run at its first member's place, groups before loose fields.
 * The builder calls it when a group box is left. The same model back when there was nothing to tidy.
 */
export function normaliseFieldsetRuns(model: GeneratedDefinition, target: string): GeneratedDefinition {
  const fields = fieldsOf(model);
  const tidied = fields.map((f) => {
    if (f.target !== target) return f;
    const title = titleOf(f);
    if (title === null) return without(f, ["fieldsetTitle", "fieldsetStartsCollapsed"]);
    return f.fieldsetTitle === title ? f : { ...f, fieldsetTitle: title };
  });
  const next = normaliseAttributeFieldsetRuns(tidied, target);
  const unchanged = next.every((f, i) => {
    const was = fields[i];
    return f.key === was.key && f.target === was.target && f.order === was.order && f.fieldsetTitle === was.fieldsetTitle && f.fieldsetStartsCollapsed === was.fieldsetStartsCollapsed;
  });
  return unchanged ? model : { ...model, nodeAttributes: next };
}

/**
 * Renames a group everywhere it's used at one level. A blank new title ungroups its members, and clears their "starts folded".
 * Renaming onto a title already in use joins the two groups; {@link normaliseFieldsetRuns} gathers them into one run.
 */
export function renameGroup(model: GeneratedDefinition, target: string, oldTitle: string, newTitle: string): GeneratedDefinition {
  const fields = fieldsOf(model);
  const renamed = renameFieldset(fields, target, oldTitle, newTitle).map((f, i) =>
    f === fields[i] || titleOf(f) !== null ? f : without(fields[i], ["fieldsetTitle", "fieldsetStartsCollapsed"]),
  );
  return renamed.every((f, i) => f === fields[i]) ? model : { ...model, nodeAttributes: renamed };
}

// ---- every section, every action point ------------------------------------------------------------------------------------

/**
 * Asks a section or action point field of every node at its level again, by clearing its scope: an empty scope means every
 * section, or every action point, including those added later. It's the way back for a field limited to sections or action
 * points that have all gone, which the server keeps on save (an empty list would mean every node) and refuses to publish.
 *
 * Refused for a whole-dial field, which is asked once and has no scope, and for an item field, whose scope has its own edits
 * ({@link setOnlySomeItems}, {@link toggleItem}). The same model back for a field already on every node, or one that isn't there.
 */
export function askOfEveryNode(model: GeneratedDefinition, target: string, key: string): GeneratedDefinition {
  if (!sameWord(target, "Section") && !sameWord(target, "Segment")) return model;
  return mapField(model, target, key, (field) => without(field, ["appliesToKeys"]));
}

// ---- only on some items ---------------------------------------------------------------------------------------------------

/** Every item in the dial, in structure order, as the qualified `section/segment/item` path an item field's scope names. */
function itemPaths(model: GeneratedDefinition): { path: string; sectionKey: string; segmentKey: string; itemKey: string }[] {
  return model.sections.flatMap((s) =>
    s.segments.flatMap((g) => g.items.map((i) => ({ path: qualifyItemScope(s.key, g.key, i.key), sectionKey: s.key, segmentKey: g.key, itemKey: i.key }))),
  );
}

/**
 * Turns "only on some items" on or off for an item field. An empty scope means every item, so turning it on ticks every item
 * straight away: those of the selected action point (`"section/segment"`) when one is given, otherwise every item in the dial. It
 * isn't turned on when that would tick nothing. Turning it off clears the scope, so the field is on every item again.
 */
export function setOnlySomeItems(model: GeneratedDefinition, key: string, on: boolean, selectedSegmentPath?: string): GeneratedDefinition {
  return mapField(model, "Item", key, (field) => {
    if (!on) return without(field, ["appliesToKeys"]);
    if (field.appliesToKeys?.length) return field;
    const [sectionKey, segmentKey] = selectedSegmentPath?.split("/") ?? [];
    const ticked = itemPaths(model)
      .filter((p) => selectedSegmentPath === undefined || (p.sectionKey === sectionKey && p.segmentKey === segmentKey))
      .map((p) => p.path);
    return ticked.length === 0 ? field : { ...field, appliesToKeys: ticked };
  });
}

/**
 * Ticks or unticks one item (`"section/segment/item"`) on an item field that's only on some items. The ticks are read as the
 * server reads the scope, legacy bare item keys included, and written back as qualified paths in structure order, as the server
 * saves them. Refuses to untick the last item: an empty scope would put the field on every item. Does nothing while the field
 * is on every item, or for an item the dial doesn't have.
 */
export function toggleItem(model: GeneratedDefinition, key: string, itemPath: string): GeneratedDefinition {
  return mapField(model, "Item", key, (field) => {
    const scope = field.appliesToKeys;
    if (!scope?.length) return field;
    const paths = itemPaths(model);
    if (!paths.some((p) => p.path === itemPath)) return field;
    const ticked = new Set(paths.filter((p) => itemScopeApplies(scope, p.sectionKey, p.segmentKey, p.itemKey)).map((p) => p.path));
    if (ticked.has(itemPath)) {
      if (ticked.size <= 1) return field;
      ticked.delete(itemPath);
    } else {
      ticked.add(itemPath);
    }
    return { ...field, appliesToKeys: paths.filter((p) => ticked.has(p.path)).map((p) => p.path) };
  });
}

/**
 * Ticks every item of one action point (`"section/segment"`) on an item field that's only on some items, keeping the items already
 * ticked elsewhere. Written back as {@link toggleItem} writes: qualified paths, in structure order. Does nothing while the field is
 * on every item, for an action point the dial doesn't have or that has no items, or when all of its items are already ticked.
 */
export function selectAllItemsIn(model: GeneratedDefinition, key: string, segmentPath: string): GeneratedDefinition {
  return mapField(model, "Item", key, (field) => {
    const scope = field.appliesToKeys;
    if (!scope?.length) return field;
    const [sectionKey, segmentKey] = segmentPath.split("/");
    const paths = itemPaths(model);
    const ticked = new Set(paths.filter((p) => itemScopeApplies(scope, p.sectionKey, p.segmentKey, p.itemKey)).map((p) => p.path));
    const adding = paths.filter((p) => p.sectionKey === sectionKey && p.segmentKey === segmentKey && !ticked.has(p.path));
    if (adding.length === 0) return field;
    for (const p of adding) ticked.add(p.path);
    return { ...field, appliesToKeys: paths.filter((p) => ticked.has(p.path)).map((p) => p.path) };
  });
}
